<template>
  <template v-if="restricted !== RESTRICTION.NOT_READY">
    <site-is-not-available-in-this-region v-if="restricted === RESTRICTION.SERVICE_NOT_ALLOWED" />
    <media-query-provider v-else :queries="$options.queries">
      <router-view />
      <custom-modal v-if="appModal.isActive" v-model="appModal.isActive" v-bind="appModal.props" />
    </media-query-provider>
  </template>
</template>

<script>
// vue
import { computed } from 'vue'
import * as Sentry from '@sentry/vue'
// configs
import { BASE_URL, getCashBoxComponent, LIST_OF_GET_PARAMS_TO_SAVE, LOCATIONS_LIST } from 'src/common/config'
// services
import { userService } from 'src/common/user-service'
import { WebsocketService } from 'src/common/websocket-service'
import { notification } from 'src/core/hooks/notifications'
import { dga } from 'src/common/websocket-dga'
// webview routes
import { webviewRoutesArray } from 'src/router/webview-routes'
// utils
import { defineApp, externalLinkMessageToApp } from 'src/common/webview'
import restFormatToGqlFormat from 'src/apollo/restFormatToGqlFormat'
import { useMetadata } from 'src/hooks/useMetadata'
import { actionToken, consoleLogAPI, defaultLang, getModal, preloaderVisible, renameKeys } from 'src/common/functions'
import { RESTRICTION } from 'src/common/dictionary'
// queries
import getCurrencyList from 'src/apollo/queries/getCurrencyList.gql'
import refreshToken from 'src/apollo/mutations/refreshToken.gql'
import getUnderDev from 'src/apollo/queries/getUnderdev.gql'
import getBonusGameIds from 'src/apollo/queries/getBonusGameIds.gql'
import getContentList from 'src/apollo/queries/getContentList.gql'
// components
import MediaQueryProvider from 'components/MediaQueryProvider.vue'
import CustomModal from 'src/core/components/customModal/CustomModal.vue'
import SiteIsNotAvailableInThisRegion from 'components/technical/SiteIsNotAvailableInThisRegion.vue'

import 'swiper/css'
import 'swiper/css/grid'
import 'swiper/css/free-mode'
import '@gamesite/olimp-core/styles/blue-theme.css'
import '@gamesite/olimp-core/styles/core.css'
import '@gamesite/olimp-core/styles/index.scss'
import FingerPrint from '@gamesite/fingerprint/dist/index'
import { TokenService } from 'src/common/token-service'
import { debounce } from 'src/common/use-events-optimisation'
import userPromo from 'src/apollo/queries/userPromo.gql'
import { getConfig } from 'src/configs'
import { mapActions } from 'pinia'
import { usePromoStore } from 'src/stores/promo'
import { useModalsStore } from 'src/stores/modals'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useSystemStore } from 'src/stores/system'
import { useUserBonusesStore } from 'src/stores/user/bonuses'
import { useUserAuthStore } from 'src/stores/user/auth'
import { useMainStore } from 'src/stores/main'
import { useHandleQueries } from 'src/hooks/query'

import(`../src/css/${process.env.PLATFORM}.variables.scss`)

const queries = {
  mobileNano: '(max-width: 360px)',
  mobileSmall: '(max-width: 575px)',
  mobile: '(max-width: 768px)',
  tab: '(min-width: 768px)',
  desktop: '(min-width: 1024px)',
}

const tokenService = new TokenService()
const config = getConfig()

if (localStorage.getItem('debug')) {
  // eslint-disable-next-line no-console
  console.log('config', config)
}

export default {
  name: 'App',
  components: { SiteIsNotAvailableInThisRegion, MediaQueryProvider, CustomModal },
  queries,
  provide() {
    return {
      queries,
      appModal: this.appModal,
      dwh: this.dwhSendEvent,
    }
  },
  setup() {
    const systemStore = useSystemStore()
    const restricted = computed(() => systemStore.restricted)

    if (restricted.value === RESTRICTION.SERVICE_NOT_ALLOWED) {
      preloaderVisible(false)
      return { restricted }
    }

    useHandleQueries()
    useMetadata()

    const isProd = process.env.NODE_ENV === 'production' && process.env.ENVIRONMENT === 'production'
    const isBot = systemStore.isBot

    if (isProd && !isBot) {
      const waitForGtm = (timeout = 7000) => {
        return new Promise((resolve) => {
          const start = Date.now()

          const checkGtmReady = () => {
            const elapsed = Date.now() - start

            if (window.dataLayer && window.dataLayer.some((event) => event.event === 'gtm.js')) {
              resolve(true)
            } else if (elapsed >= timeout) {
              resolve(false)
            } else {
              setTimeout(checkGtmReady, 1000)
            }
          }

          checkGtmReady()
        })
      }

      waitForGtm().then(() => {
        try {
          new FingerPrint(`${BASE_URL}/fp`).init(useUserAuthStore().getUser?.id, localStorage.getItem('a_ssid'))
        } catch (e) {
          console.error(e)
        }
      })
    }

    return { restricted }
  },
  data() {
    return {
      appModal: {
        isActive: false,
        props: null,
        dwhError: (event) => {
          this.dwhSendEvent(event)
        },
        open: function (props) {
          this.isActive = true
          return new Promise((resolve, reject) => {
            this.props = { ...props, actions: props.actions, resolve, reject }
          })
            .catch(() => {
              if (props.props?.tab === 'login') {
                this.dwhError('click_close_auth_form')
              } else if (props.props?.tab === 'reg') {
                this.dwhError('click_close_reg_form')
              }
            })
            .finally(() => {
              this.props = null
            })
        },
        close: function () {
          this.isActive = false
        },
      },
    }
  },
  computed: {
    RESTRICTION() {
      return RESTRICTION
    },
    getAuth() {
      return useUserAuthStore().getAuth
    },
    getUser() {
      return useUserAuthStore().getUser
    },
    isLiveChatPage() {
      return this.$route.name === 'livechat'
    },
    isWebViewPage() {
      return webviewRoutesArray.map((item) => item.name).includes(this.$route.name)
    },
  },
  watch: {
    '$i18n.locale': {
      handler(val) {
        document.documentElement.setAttribute('lang', LOCATIONS_LIST[val] || val)
      },
      immediate: true,
    },
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (newQuery.modal && newQuery.modal !== oldQuery.modal) {
          this.openQueryModal(newQuery.modal)
        }
      },
      deep: true,
    },
  },
  beforeCreate() {
    try {
      Sentry.setTag('platform', process.env.PLATFORM)
      Sentry.setTag('webview', process.env.WEBVIEW === '1')
    } catch {
      console.warn('Cannot set tags to sentry')
    }
  },
  beforeMount() {
    this.fetchBonusGameIds = debounce(this.fetchBonusGameIds, 1500)
  },
  mounted() {
    if (this.isWebViewPage || this.restricted) {
      return
    }

    useMainStore().updateMainGroups()

    this.setGtmUser()

    this.$router
      .isReady()
      .then(() => {
        if (this.isLiveChatPage) {
          return
        }

        if (this.$route.query.promoreg) {
          localStorage.setItem(
            'promoreg',
            JSON.stringify({
              promoreg: this.$route.query.promoreg,
              date: Date.now(),
            })
          )
        }

        if (this.$route.query.cinn) {
          localStorage.setItem('cinn', '1')
        }
        if (this.$route.query.v) {
          localStorage.setItem('appVersion', this.$route.query.v)
        }
        if (!localStorage.getItem('u_lang') && this.$route.params.locale) {
          localStorage.setItem('u_lang', this.$route.params.locale.toUpperCase())
        }

        this.initWS()
        this.initWsDGA()
        this.fetchCurrencyList()
        this.fetchPromoTypes()
        if (this.getAuth) {
          this.fetchPromoList()
        }
        if (this.getAuth && this.$route.hash === '#payment_decline' && this.$route.query.old_money_order_uuid) {
          const oldMoneyOrderUuid = this.$route.query.old_money_order_uuid
          this.openQueryModal('repeatTopUpModal', { oldMoneyOrderUuid })
        }
        if (this.$route.query.modal) {
          this.$axios.dwh
            .sendEvent('registration_initiated_modal', useSiteInfoStore().getCountryCode)
            .catch(consoleLogAPI)
          this.openQueryModal(this.$route.query.modal)
        }
        window.addEventListener('message', this.listenIframe)
        if (useSiteInfoStore().isWebView) {
          window.addEventListener('click', this.handleClickToCatchExternalLinks)
        }

        this.clearQueryParams()
      })
      .catch((e) => {
        console.error(e)
      })
  },
  beforeUnmount() {
    window.removeEventListener('message', this.listenIframe)
    window.removeEventListener('click', this.handleClickToCatchExternalLinks)
  },
  methods: {
    ...mapActions(usePromoStore, {
      setPromoTypes: 'setPromoTypes',
    }),

    setGtmUser() {
      if (!this.getUser?.id) {
        return
      }

      if (process.env.GTM) {
        this.$gtm.push({
          event: 'userLoggedIn',
          user_id: this.getUser.id,
        })
      }
    },
    openCashBox() {
      this.appModal.open({ componentName: getCashBoxComponent() })
    },
    dwhSendEvent(event, params = {}) {
      if (!config.useDWH) {
        return
      }

      const isIosWebview = location.hostname === 'apps-olymp.xyz'

      const origin = {
        APP_IOS: 'APP_IOS',
        APP_ANDROID: 'APP_ANDROID',
        BROWSER_MOBILE: 'BROWSER_MOBILE',
        BROWSER_DESKTOP: 'BROWSER_DESKTOP',
      }

      let device = 'UNDEFINED'

      if (defineApp() === 'map' && useSiteInfoStore().isWebView) {
        device = origin.APP_ANDROID
      } else if (defineApp() === 'mav' && useSiteInfoStore().isWebView) {
        device = origin.APP_IOS
      } else if (isIosWebview && useSiteInfoStore().isWebView) {
        device = origin.APP_IOS
      } else if (useSystemStore().isMobile && !useSiteInfoStore().isWebView) {
        device = origin.BROWSER_MOBILE
      } else if (!useSystemStore().isMobile && !useSiteInfoStore().isWebView) {
        device = origin.BROWSER_DESKTOP
      }

      this.$axios.dwh.sendEvent(event, useSiteInfoStore().getCountryCode, device, params).catch(consoleLogAPI)
    },
    handleClickToCatchExternalLinks(event) {
      const target = event.target.closest('a')
      if (target && target.href) {
        const url = new URL(target.href)
        if (url.origin !== window.location.origin) {
          event.preventDefault()
          externalLinkMessageToApp(url.toString())
        }
      }
    },
    openQueryModal(modalName) {
      const modal = getModal(modalName, this.getAuth)
      if (modal) {
        const { componentName, props } = modal
        this.appModal
          .open({ componentName: componentName(), props })
          .finally(() => this.$router.replace({ query: null }))
      }
    },
    listenIframe(event) {
      const eventData = event?.data
      if (!eventData) {
        return
      }
      if (eventData === 'geo_blocking_get_lang') {
        event.source.postMessage({ name: 'geo_blocking_lang', lang: this.$i18n.locale }, '*')
      }

      if (eventData === 'geo_blocking_back') {
        const prevRoute = useSystemStore().prevRoute?.name ? useSystemStore().prevRoute : { name: 'Home' }
        this.$router.replace(prevRoute).catch(() => {})
      }

      const arrayHiddenTypes = ['webpackInvalid', 'application/x-postmate-v1+json', 'action-hook-fired']
      const arrayHiddenMessage = ['action-hook-fired', 'reload-tab-complete']

      if (
        localStorage.getItem('show-events') &&
        !arrayHiddenTypes.includes(eventData?.type) &&
        !arrayHiddenMessage.includes(eventData?.message)
      ) {
        // eslint-disable-next-line no-console
        console.log('Event data from iframe.', eventData)
      }

      const pushEvent = eventData.push && typeof eventData.push === 'string'
      const payload = eventData.payload || eventData.value
      const actionEventGame = eventData.action === 'game.location' && payload

      if (pushEvent || actionEventGame) {
        if (localStorage.getItem('show-events')) {
          // eslint-disable-next-line no-console
          console.log('event', event)
          // eslint-disable-next-line no-console
          console.log('pushEvent', pushEvent)
          // eslint-disable-next-line no-console
          console.log('actionEventGame', actionEventGame)
        }
        let unresolvedUrl = pushEvent ? eventData.push : payload

        if (unresolvedUrl.startsWith('http')) {
          try {
            const parsedURL = new URL(unresolvedUrl)
            unresolvedUrl = parsedURL.pathname + parsedURL.search
          } catch (e) {
            console.error(e)
          }
        }

        try {
          if (BASE_URL && unresolvedUrl.startsWith(BASE_URL)) {
            unresolvedUrl = unresolvedUrl.replace(BASE_URL, '')
          }
          const newRoute = this.$router.resolve(unresolvedUrl)

          if (
            newRoute &&
            newRoute.fullPath !== this.$route.fullPath &&
            !['Auth', 'AuthEmail'].includes(newRoute.name)
          ) {
            return this.$router.replace(newRoute).catch(() => {})
          }
          this.$router.replace({ name: 'Home' }).catch(() => {})
        } catch {
          this.$router.replace({ name: 'Home' }).catch(() => {})
        }
      }
    },

    initWS() {
      const auth = this.getAuth
      const token = auth ? useUserAuthStore().getToken : ''
      WebsocketService.initWebsocket(token)
      this.subscribeToWebsocket(this.getCommonWebsocketCallbacks())
      this.subscribeToWebsocket(this.getUserWebsocketCallbacks())
    },

    initWsDGA() {
      if (process.env.DGA_URI && process.env.DGA_CASINO_ID) {
        dga.connect(process.env.DGA_URI, process.env.DGA_CASINO_ID, useSiteInfoStore().userCurrency?.code)
      }
    },

    subscribeToWebsocket(callbacks) {
      callbacks.forEach(({ message_type, callback }) => {
        WebsocketService.subscribeToSocketMessage(message_type, (content) => {
          callback(content)
        })
      })
    },

    getNotificationType(notification_type) {
      switch (notification_type) {
        case 1:
          return 'info'
        case 2:
          return 'warning'
        case 3:
          return 'success'
        default:
          return 'info'
      }
    },

    getCommonWebsocketCallbacks() {
      return [
        {
          message_type: 'update_settings_underdev',
          callback: (content) => {
            if (useSystemStore().underdev !== content.underdev) {
              if (content.underdev) {
                notification({ type: 'warning', text: this.$t('engineeringWorks') })

                setTimeout(() => {
                  this.$apollo
                    .query({
                      query: getUnderDev,
                      fetchPolicy: 'network-only',
                    })
                    .then(({ data }) => {
                      if (data?.siteInfo?.underDev) {
                        useSystemStore().updateUnderDev(data.siteInfo.underDev)
                        this.$router.push({ name: 'Home' })
                      }
                    })
                    .catch(consoleLogAPI)
                }, 300000)
              } else {
                location.reload()
              }
            }
          },
        },
      ]
    },

    getUserWebsocketCallbacks() {
      const notificationFn = (content) => {
        notification({
          type: this.getNotificationType(content.notification_type),
          text: content.text,
          timer: content.notification_time,
          canClose: content.notification_close,
        })
      }

      const updateMessagesFn = () => {
        //TODO store // в сторе нет этого экшена
        // this.$store.dispatch('updateMessageContent', content)
      }

      const logoutFn = () => {
        useUserAuthStore().removeToken()
        userService.logout()
        const locale = this.$i18n.locale === defaultLang ? '' : this.$i18n.locale
        window.location.href = BASE_URL + '/' + locale
      }

      const deleteMessageFn = () => {
        //TODO store // в сторе нет этого экшена
        // this.$store.dispatch('deleteMessage', content)
      }

      return [
        {
          message_type: 'token_will_expire',
          callback: async () => {
            const token = useUserAuthStore().getToken
            const actionTokenType = actionToken(token)

            if (actionTokenType === 'refresh') {
              await this.$apollo
                .mutate({
                  mutation: refreshToken,
                  variables: { token },
                })
                .then(({ data }) => {
                  useUserAuthStore().updateToken(data.refreshToken.token)
                  tokenService.setToken(data.refreshToken.token)
                })
                .catch((e) => {
                  consoleLogAPI(e)
                  logoutFn()
                })
            } else if (actionTokenType === 'remove') {
              console.error('Token was removed. App.vue | ws | token_will_expire | action = remove')
              logoutFn()
            }
          },
        },
        {
          message_type: 'update_turnover',
          callback: (content) => {
            useUserAuthStore().updateUserField({ key: 'threshold', value: content })
          },
        },
        {
          message_type: 'update_fast_cashback',
          callback: (content) => {
            useUserAuthStore().updateCashback({
              amount: content.amount,
              spinsCount: content.spins_count,
            })
          },
        },
        {
          message_type: 'update_program',
          callback: (content) => {
            // отправляется при изменении Бонусной программы пользователям, у которых есть такой бонус
            useUserBonusesStore().updateProgram(content)
          },
        },
        {
          message_type: 'update_user_count_active_withdraws',
          callback: (content) => {
            useUserAuthStore().updateUserWithdrawalsRequests(content?.count_active_withdraw)
          },
        },
        {
          message_type: 'update_user_program',
          callback: (content) => {
            // отправляется пользователю при получении и активации бонуса
            useUserBonusesStore().updateUserProgram(renameKeys(content))
          },
        },
        {
          message_type: 'delete_user_program',
          callback: (content) => {
            useUserBonusesStore().deleteUserProgram(renameKeys(content))
          },
        },
        {
          message_type: 'update_user_step',
          callback: (content) => {
            const data = content ? renameKeys(content) : null
            if (data && [2, 4, 6].includes(data?.condition)) {
              this.fetchBonusGameIds()
            }

            useUserBonusesStore().updateActiveStep(data)

            if (data?.propsContent?.[0]?.props?.casOpen?.value) {
              const action = data?.propsContent[0]?.props?.casOpen?.value

              if (
                action == 1 ||
                (action == 2 && !useSystemStore().isMobile) ||
                (action == 3 && useSystemStore().isMobile)
              ) {
                this.appModal.open({ componentName: getCashBoxComponent() })
              }
            }
          },
        },
        {
          message_type: 'update_user_step_balance',
          callback: (content) => {
            // отправляется пользователю при смене поля balance в активном шаге
            useUserBonusesStore().updateUserStepBalance(content)
          },
        },
        {
          message_type: 'update_promo_freespin_available',
          callback: (content) => {
            const result = content?.map((i) => {
              return {
                id: i.id,
                activated: i.activated,
                game: {
                  manufacturer: {
                    code: i.game_obj.manufacturer_code,
                  },
                  slug: i.game_obj.slug,
                  name: i.game_obj.name,
                  compressedImage: i.game_obj.compressed_image,
                },
                campaignId: i.campaign_id,
                programId: i.program_id,
                freespinsCount: i.freespins_count,
                freespinsLeft: i.freespins_left,
                dateExpire: i.date_end,
              }
            })
            useUserBonusesStore().updatePromoFreespinAvailable(result || [])
          },
        },
        {
          message_type: 'promo_max_bet_exceeded',
          callback: (content) => {
            this.$helpers.notify({
              color: 'cas-err',
              type: 'negative',
              icon: 'las la-exclamation-triangle',
              message: this.$t('promoMaxBetExceeded', {
                bet: content.max_bet,
                currency: useSiteInfoStore().userCurrency?.code,
              }),
            })
          },
        },
        {
          message_type: 'update_user_free_bets',
          callback: (content) => {
            useUserBonusesStore().updateFreeBets(content.count)
          },
        },
        {
          message_type: 'update_documents_status',
          callback: (content) => {
            const newDocsStatus = content.docs_verification

            if (useUserAuthStore().getUserVerificationStatus !== newDocsStatus) {
              // NotUploaded = 0, "Не загружены"
              // Verification = 1, "Проверка документов"
              // VerifiedAndValid = 2, "Проверены и валидны"
              // Declined = 3, "Отклонены"
              if (newDocsStatus === 2) {
                notification({ type: 'success', text: this.$t('docsWasApproved') })
              } else if (newDocsStatus === 3) {
                notification({ type: 'warning', text: this.$t('docsWasDeclined') })
              }
            }
            useUserAuthStore().updateUserFields({
              docsVerificationId: content.docs_verification,
              needVerification: content.need_verification,
              withdrawAccess: content.withdraw_access,
              withdrawAccessStr: content.withdraw_access_str,
            })
          },
        },
        {
          message_type: 'delete_message_for_all_users',
          callback: deleteMessageFn,
        },
        {
          message_type: 'delete_for_user',
          callback: deleteMessageFn,
        },
        {
          message_type: 'block_user',
          callback: () => {
            console.error('Token was removed. App.vue | ws | block_user')
            logoutFn()
          },
        },
        {
          message_type: 'throwout_user',
          callback: () => {
            console.error('Token was removed. App.vue | ws | throwout_user')
            logoutFn()
          },
        },
        {
          message_type: 'throwout_all_users',
          callback: () => {
            console.error('Token was removed. App.vue | ws | throwout_all_users')
            logoutFn()
          },
        },
        {
          message_type: 'update_message_for_all_users',
          callback: updateMessagesFn,
        },
        {
          message_type: 'update_for_user',
          callback: updateMessagesFn,
        },
        {
          message_type: 'notification_for_all_users',
          callback: notificationFn,
        },
        {
          message_type: 'notification_for_user',
          callback: notificationFn,
        },
        {
          message_type: 'update_balance',
          callback: (content) => {
            if (this.getUser.bonusBalanceOn !== content.bonus_balance_on) {
              const messageText = content.bonus_balance_on ? this.$t('BonusBalanceOn') : this.$t('BonusBalanceOff')
              const actions = [
                {
                  label: content.bonus_balance_on ? this.$t('play') : this.$t('depositAccount'),
                  handler: () =>
                    content.bonus_balance_on ? this.$router.push({ name: 'BonusGames' }) : this.openCashBox(),
                },
              ]
              notification({ text: messageText, timer: 4000, actions })
            }
            if (content.bonus_balance_on && this.$route.name === 'Sports') {
              useModalsStore().setSportModalStatus(true)
            }
            useUserAuthStore().updateUserFields({
              balance: content.balance,
              bonusBalance: content.bonus_balance,
              bonusBalanceOn: content.bonus_balance_on,
            })
          },
        },
        {
          message_type: 'update_balance_bk',
          callback: (content) => {
            useUserAuthStore().updateUserField({
              key: 'bkBalance',
              value: content.balance_bk,
            })
          },
        },
        {
          message_type: 'update_vager_info',
          callback: (content) => {
            useUserAuthStore().updateUserField({
              key: 'activeVager',
              value: content.active_vager,
            })
            useUserAuthStore().updateUserField({
              key: 'vagerBonus',
              value: content.vager_sum,
            })
          },
        },
        {
          message_type: 'update_user_info',
          callback: (content) => {
            if (this.getAuth) {
              useUserAuthStore().updateUser(restFormatToGqlFormat(content))
            }
          },
        },
        {
          message_type: 'signout',
          callback: () => {
            console.error('Token was removed. App.vue | ws | signout')
            logoutFn()
          },
        },
      ]
    },
    fetchPromoList() {
      this.$apollo
        .query({
          query: userPromo,
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          useUserBonusesStore().updateProgramAvailable(data?.userPromo?.programsAvailable ?? [])
          useUserBonusesStore().updateActiveStepLoaded()

          if (data?.userPromo?.activeStep) {
            useUserBonusesStore().updateActiveStep(data.userPromo.activeStep)
            useUserBonusesStore().updateUserProgram(data.userPromo.activeStep.userProgram)
            useUserBonusesStore().updateBonusGameIdsList(data.userPromo.activeStep.gameIdsList)
          }
        })
        .catch((e) => {
          consoleLogAPI(e)
        })
    },

    fetchBonusGameIds() {
      this.$apollo
        .query({
          query: getBonusGameIds,
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          useUserBonusesStore().updateBonusGameIdsList(data?.userPromo?.activeStep?.gameIdsList ?? null)
        })
        .catch(consoleLogAPI)
    },
    fetchCurrencyList() {
      this.$apollo
        .query({
          query: getCurrencyList,
        })
        .then(({ data }) => {
          useSiteInfoStore().setCurrencyData(data)
        })
        .catch(consoleLogAPI)
    },
    fetchPromoTypes() {
      this.$apollo
        .query({
          query: getContentList,
          variables: {
            type: 'promotion_types_v2',
          },
        })
        .then(({ data }) => {
          const { propsContentList } = data
          const promoTypes = propsContentList
            .map((promoType) => ({ id: promoType.id, ...JSON.parse(promoType.props) }))
            .sort((a, b) => a.id - b.id)

          this.setPromoTypes(promoTypes)
        })
        .catch(consoleLogAPI)
    },
    clearQueryParams() {
      const query = { ...this.$route.query }
      let hasParams = false
      for (const param in LIST_OF_GET_PARAMS_TO_SAVE) {
        if (query[param]) {
          hasParams = true
          delete query[param]
        }
      }
      if (hasParams) {
        this.$router.replace({ query })
      }
    },
  },
}
</script>

<style lang="scss">
body {
  background: var(--cas-dark-2);
}
</style>
